import { Link } from 'react-router-dom';
import durianImg from '../image/2.png';
import { AnimatedCard } from '../components/ui/AnimatedCard';
import { StarRating } from '../components/ui/StarRating';
import { SectionLink } from '../components/ui/SectionLink';
import { CTAButton } from '../components/ui/CTAButton';
import { BRAND_VALUES, FEATURES, PRODUCTS, TESTIMONIALS } from '../constants/content';
import { featurePath, meditationPath, inquiryPath, productPath } from '../constants/navigation';

export default function HomePage() {
  return (
    <>
      <section className="container mx-auto px-4 py-16 md:py-24 flex flex-col md:flex-row items-center relative overflow-hidden">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="md:w-1/2 mb-10 md:mb-0 relative z-10">
          <p className="inline-flex items-center gap-2 text-sm text-purple-300 bg-white/5 border border-purple-500/30 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            AI 冥想 · 萌宠陪伴 · 正念生活
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            专属治愈 <span className="text-gradient">冥想喵星之旅</span>
          </h1>
          <p className="text-lg text-purple-200 mb-4 max-w-lg leading-relaxed">
            依托 AI 智能冥想体系与萌趣毛绒周边，为情感疗愈、职场减压、睡眠放松与深度内观提供专属引导。每一次呼吸，都是与内心的温柔对话。
          </p>
          <p className="text-purple-300 text-sm mb-8 max-w-lg">
            融合专业冥想引导与现代 AI 陪伴，让正念练习触手可及，让每一天都更平静、更有力量。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <CTAButton to="/guide" variant="primary" size="lg" icon="fa-play">
              开始冥想
            </CTAButton>
            <CTAButton to="/products" variant="secondary" size="lg">
              探索周边
            </CTAButton>
          </div>
          <div className="mb-10">
            <CTAButton to="/inquiry" variant="outline" size="sm" icon="fa-shopping-cart">
              购买咨询
            </CTAButton>
          </div>
          <div className="flex flex-wrap gap-8 text-center sm:text-left">
            <div>
              <p className="text-2xl font-bold text-gradient">4+</p>
              <p className="text-purple-300 text-sm">冥想主题</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gradient">AI</p>
              <p className="text-purple-300 text-sm">智能引导</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gradient">24h</p>
              <p className="text-purple-300 text-sm">随时练习</p>
            </div>
          </div>
        </div>
        <div className="md:w-1/2 flex justify-center relative z-10">
          <div className="relative w-full max-w-md">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary/30 to-pink-500/20 rounded-full blur-3xl" />
            <AnimatedCard className="relative bg-white/5 backdrop-blur-sm rounded-3xl border border-purple-500/30 p-8">
              <div className="flex justify-center mb-6">
                <img
                  src={durianImg}
                  alt="可爱榴莲水果毛绒公仔"
                  className="w-48 h-48 object-contain drop-shadow-2xl"
                />
              </div>
              <div className="text-center space-y-3">
                <p className="text-xs uppercase tracking-widest text-purple-400">
                  人气周边 · 水果系列
                </p>
                <p className="text-xl font-semibold">可爱榴莲毛绒公仔</p>
                <p className="text-purple-200 text-sm leading-relaxed">
                  金黄果肉、翠绿尖刺与甜美笑脸，陪伴你的每一次冥想时光。
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
                  <SectionLink to={productPath(PRODUCTS[0].slug)}>了解详情</SectionLink>
                  <SectionLink to={inquiryPath(PRODUCTS[0].slug)}>购买咨询</SectionLink>
                </div>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">关于 Ameowzing</h2>
          <p className="text-purple-200 leading-relaxed mb-6">
            我们相信，冥想不是逃避现实，而是更好地与自己相处。Ameowzing 将专业冥想引导与现代 AI
            技术相结合，用温暖可爱的形象，陪伴你在忙碌生活中找到属于自己的平静角落。
          </p>
          <SectionLink to="/about">阅读完整品牌故事</SectionLink>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {BRAND_VALUES.map((item) => (
            <AnimatedCard
              key={item.title}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 text-center"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <i className={`fa ${item.icon} text-secondary text-2xl`} />
              </div>
              <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
              <p className="text-purple-200 text-sm">{item.desc}</p>
            </AnimatedCard>
          ))}
        </div>
      </section>

      <OverviewSection
        title="全维度冥想指引"
        desc="选择对应主题，即刻获取专属引导课程与练习建议。"
        linkTo="/features"
        linkLabel="查看全部功能"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {FEATURES.map((f) => (
            <Link
              key={f.id}
              to={featurePath(f.id)}
              className="link-card bg-white/5 rounded-xl p-4 border border-purple-500/20 text-center"
            >
              <i className={`fa ${f.icon} ${f.iconColor} text-xl mb-2`} />
              <p className="font-medium text-sm">{f.title}</p>
            </Link>
          ))}
        </div>
      </OverviewSection>

      <OverviewSection
        title="冥想课程"
        desc="从 5 分钟快速练习到 30 分钟深度旅程，匹配你的时间与状态。"
        linkTo="/meditation"
        linkLabel="了解全部课程"
        className="bg-white/5 rounded-3xl my-8 py-16"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto text-center">
          {[
            { id: 'breath', title: '5 分钟呼吸冥想' },
            { id: 'theme', title: '15 分钟主题冥想' },
            { id: 'deep', title: '30 分钟深度冥想' },
          ].map((item) => (
            <Link
              key={item.id}
              to={meditationPath(item.id)}
              className="link-card bg-darker/40 rounded-xl p-4 border border-purple-500/20"
            >
              <p className="font-medium">{item.title}</p>
            </Link>
          ))}
        </div>
      </OverviewSection>

      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">用户心声</h2>
        <p className="text-purple-200 text-center mb-12 max-w-2xl mx-auto">
          来自真实用户的反馈，见证 Ameowzing 如何陪伴每一次正念练习。
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {TESTIMONIALS.map((item) => (
            <AnimatedCard
              key={item.author}
              as="blockquote"
              className="bg-white/5 rounded-2xl p-6 border border-purple-500/20"
            >
              <StarRating stars={item.stars} />
              <p className="text-purple-200 text-sm mb-4 leading-relaxed">{item.quote}</p>
              <footer className="text-purple-400 text-sm">{item.author}</footer>
            </AnimatedCard>
          ))}
        </div>
      </section>

      <OverviewSection
        title="萌趣冥想随行伙伴"
        desc="水果系列毛绒公仔，为你的冥想空间增添温暖陪伴与治愈仪式感。"
        linkTo="/products"
        linkLabel="浏览全部产品"
        className="bg-white/5 rounded-3xl my-8 py-16"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {PRODUCTS.map((product) => (
            <div
              key={product.slug}
              className="link-card bg-white/5 rounded-2xl p-4 border border-purple-500/20"
            >
              <Link to={productPath(product.slug)} className="flex items-center gap-4 mb-3">
                <img src={product.image} alt={product.title} className="w-20 h-20 object-contain" />
                <div>
                  <p className="font-semibold">{product.title}</p>
                  <p className="text-purple-300 text-sm">{product.shortDesc}</p>
                </div>
              </Link>
              <div className="flex gap-4 pl-24">
                <Link to={productPath(product.slug)} className="text-secondary text-xs hover:underline">
                  详情
                </Link>
                <Link to={inquiryPath(product.slug)} className="text-secondary text-xs hover:underline">
                  购买咨询
                </Link>
              </div>
            </div>
          ))}
        </div>
      </OverviewSection>

      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {[
            { to: '/guide', icon: 'fa-play-circle', title: '使用教程', desc: '四步上手冥想' },
            { to: '/inquiry', icon: 'fa-shopping-cart', title: '购买咨询', desc: '周边采购报价' },
            { to: '/faq', icon: 'fa-question-circle', title: '常见问题', desc: '快速找到答案' },
            { to: '/contact', icon: 'fa-envelope', title: '联系我们', desc: '商务与合作咨询' },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="link-card bg-white/5 rounded-2xl p-8 border border-purple-500/20"
            >
              <i className={`fa ${item.icon} text-3xl text-secondary mb-3`} />
              <h3 className="font-semibold mb-2">{item.title}</h3>
              <p className="text-purple-300 text-sm">{item.desc}</p>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}

function OverviewSection({ title, desc, linkTo, linkLabel, children, className = '' }) {
  return (
    <section className={`container mx-auto px-4 py-16 ${className}`}>
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">{title}</h2>
      <p className="text-purple-200 text-center mb-8 max-w-2xl mx-auto">{desc}</p>
      {children}
      <div className="text-center mt-8">
        <SectionLink to={linkTo}>{linkLabel}</SectionLink>
      </div>
    </section>
  );
}
